/**
 * Altura que sobra do topo de um elemento até o fundo do `<main>` que rola, para as
 * colunas rolarem cada uma por conta própria: explorer da KB (card #25437) e Entrada de
 * chamados (pedido de 21/10/2026).
 *
 * Medida, e não `calc(100vh - X)`: o que fica acima (cabeçalho, trilha, abas, o aviso
 * de árvore desatualizada) muda de altura, e um X fixo erraria em silêncio. E o fundo é
 * o do `<main>`, não o da janela: há coisa abaixo dele (medido em 21/10: o `<main>`
 * acabava 34 px antes da janela, e a página ainda rolava).
 */
import { useLayoutEffect, useState } from 'react'

/** Abaixo disto a coluna não cabe: a página volta a rolar inteira. */
export const ALTURA_MINIMA = 320

export function alturaAteOFim(topo: number, fundo: number): number {
  return Math.max(ALTURA_MINIMA, Math.floor(fundo - topo))
}

/** Fundo útil do contêiner que rola: o `<main>` menos o padding de baixo dele. */
function fundoDoMain(el: HTMLElement): { fundo: number; main: HTMLElement | null } {
  const main = el.closest('main')
  if (!main) return { fundo: window.innerHeight, main: null }
  const padding = parseFloat(getComputedStyle(main).paddingBottom) || 0
  return { fundo: main.getBoundingClientRect().bottom - padding, main }
}

/**
 * `ref` é de callback de propósito: a tela monta as colunas só depois de a árvore
 * carregar, e um `useRef` com efeito de deps fixas mediria um elemento que ainda não existe.
 */
export function useAlturaAteOFim<T extends HTMLElement>(ligado: boolean) {
  const [el, ref] = useState<T | null>(null)
  const [altura, setAltura] = useState<number | null>(null)
  useLayoutEffect(() => {
    if (!ligado || !el) {
      setAltura(null)
      return
    }
    const medir = () => {
      const nova = alturaAteOFim(el.getBoundingClientRect().top, fundoDoMain(el).fundo)
      setAltura((atual) => (atual === nova ? atual : nova))
    }
    medir()
    window.addEventListener('resize', medir)
    // O que fica acima muda de altura sem a janela mudar (aviso, aba), e o `<main>`
    // muda com a barra lateral ou o banner.
    const observador = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null
    if (observador) {
      if (el.parentElement) observador.observe(el.parentElement)
      const { main } = fundoDoMain(el)
      if (main) observador.observe(main)
    }
    return () => {
      window.removeEventListener('resize', medir)
      observador?.disconnect()
    }
  }, [ligado, el])
  return { ref, altura }
}
